import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows } from "@/lib/adminApi";
import type { Payment } from "@/lib/adminCommerce";
import {
  DataTable,
  ErrorBanner,
  LoadingState,
  PageHeader,
  StatCards,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import { aggregatePaymentsByMonth, type MonthRevenue } from "@/lib/adminReports";

const COMMISSION_RATE = 0.15;

interface FarmRevenue {
  id: number;
  farm: string;
  collected: number;
  commission: number;
  payout: number;
  transactions: number;
}

export default function AdminRevenue() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPayments(await listRows<Payment>("payments", "paid_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load revenue data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monthly = useMemo(() => aggregatePaymentsByMonth(payments, 12), [payments]);

  const totals = useMemo(
    () =>
      payments.reduce(
        (acc, payment) => {
          const amount = Number(payment.amount || 0);
          if (payment.status === "paid") acc.collected += amount;
          else if (payment.status === "pending") acc.pending += amount;
          else if (payment.status === "refunded") acc.refunded += amount;
          return acc;
        },
        { collected: 0, pending: 0, refunded: 0 },
      ),
    [payments],
  );

  const farmRevenue = useMemo<FarmRevenue[]>(() => {
    const map = new Map<string, FarmRevenue>();
    payments
      .filter((payment) => payment.status === "paid")
      .forEach((payment) => {
        const farm = payment.farm_name || "Unassigned";
        const existing = map.get(farm) || { id: 0, farm, collected: 0, commission: 0, payout: 0, transactions: 0 };
        existing.collected += Number(payment.amount || 0);
        existing.transactions += 1;
        map.set(farm, existing);
      });
    return Array.from(map.values())
      .map((row) => ({
        ...row,
        commission: Math.round(row.collected * COMMISSION_RATE),
        payout: Math.round(row.collected * (1 - COMMISSION_RATE)),
      }))
      .sort((a, b) => b.collected - a.collected)
      .map((row, index) => ({ ...row, id: index + 1 }));
  }, [payments]);

  const commission = Math.round(totals.collected * COMMISSION_RATE);
  const payouts = totals.collected - commission;

  const monthColumns: Column<MonthRevenue>[] = [
    { key: "month", header: "Month", primary: true, render: (row) => <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.label}</span> },
    { key: "collected", header: "Gross", render: (row) => <span className="text-sm text-foreground-800 whitespace-nowrap">{formatRupees(row.collected)}</span> },
    { key: "commission", header: "Commission", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(Math.round(row.collected * COMMISSION_RATE))}</span> },
    { key: "payout", header: "Farmer payout", render: (row) => <span className="text-sm text-foreground-800 whitespace-nowrap">{formatRupees(Math.round(row.collected * (1 - COMMISSION_RATE)))}</span> },
    { key: "refunded", header: "Refunds", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.refunded)}</span> },
    { key: "txns", header: "Txns", render: (row) => <span className="text-sm text-foreground-700">{row.transactions}</span> },
  ];

  const farmColumns: Column<FarmRevenue>[] = [
    { key: "farm", header: "Farm", primary: true, render: (row) => <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farm}</span> },
    { key: "collected", header: "Gross", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.collected)}</span> },
    { key: "commission", header: "Commission", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.commission)}</span> },
    { key: "payout", header: "Farmer payout", render: (row) => <span className="text-sm text-foreground-800 whitespace-nowrap">{formatRupees(row.payout)}</span> },
    { key: "txns", header: "Txns", render: (row) => <span className="text-sm text-foreground-700">{row.transactions}</span> },
  ];

  const monthExport = [
    { header: "Month", value: (row: MonthRevenue) => row.label },
    { header: "Gross", value: (row: MonthRevenue) => `${row.collected}` },
    { header: "Commission", value: (row: MonthRevenue) => `${Math.round(row.collected * COMMISSION_RATE)}` },
    { header: "Farmer payout", value: (row: MonthRevenue) => `${Math.round(row.collected * (1 - COMMISSION_RATE))}` },
    { header: "Refunds", value: (row: MonthRevenue) => `${row.refunded}` },
  ];
  const farmExport = [
    { header: "Farm", value: (row: FarmRevenue) => row.farm },
    { header: "Gross", value: (row: FarmRevenue) => `${row.collected}` },
    { header: "Commission", value: (row: FarmRevenue) => `${row.commission}` },
    { header: "Farmer payout", value: (row: FarmRevenue) => `${row.payout}` },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Commerce"
        title="Revenue"
        subtitle="What the platform earns and what flows to farmers, split by month and by farm."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-revenue-monthly", monthExport, monthly)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Revenue Overview", subtitle: "Monthly gross, commission and payouts", columns: monthExport, rows: monthly })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Gross collected", value: formatRupees(totals.collected), icon: "ri-money-rupee-circle-line", tone: "primary" },
          { label: "Platform commission", value: formatRupees(commission), icon: "ri-percent-line", tone: "secondary", hint: `${Math.round(COMMISSION_RATE * 100)}% of gross` },
          { label: "Farmer payouts", value: formatRupees(payouts), icon: "ri-hand-coin-line", tone: "primary" },
          { label: "Pending / refunded", value: formatRupees(totals.pending + totals.refunded), icon: "ri-time-line", tone: "accent" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      {loading ? (
        <div className="mt-5">
          <LoadingState label="Loading revenue…" />
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          <section>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-3">Month by month</h2>
            <DataTable columns={monthColumns} rows={monthly} />
          </section>
          <section>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-3">Revenue by farm</h2>
            <DataTable columns={farmColumns} rows={farmRevenue} />
          </section>
        </div>
      )}
    </div>
  );
}